import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, Building2, CircleAlert } from 'lucide-react';

import { VoipHealthCard } from '@apps/web/components/admin/accounts/voip_health_card';
import { VoipSetupAssistant } from '@apps/web/components/admin/voip/voip_setup_assistant';
import { VOIP_PHONE_PROVIDER_LABELS } from '@apps/web/components/admin/voip/voip_utils';
import {
    Alert,
    AlertDescription,
    AlertTitle,
} from '@apps/web/components/ui/alert';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Card, CardContent } from '@apps/web/components/ui/card';
import { DetailRow } from '@apps/web/components/ui/detail_row';
import { Spinner } from '@apps/web/components/ui/spinner';
import { useAdminOrganizationVoipSetup } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export const Route = createFileRoute('/_app/admin/voip/$organization-id')({
    head: () => ({
        meta: [{ title: 'Voice Integration | Phoebe Admin' }],
    }),
    component: OrganizationVoipSetupPage,
});

function OrganizationVoipSetupPage() {
    const { 'organization-id': organizationId } = Route.useParams();
    const {
        data: setup,
        isLoading,
        error,
    } = useAdminOrganizationVoipSetup(organizationId);

    if (isLoading) {
        return (
            <div className="text-muted-foreground flex h-40 items-center justify-center gap-2 text-sm">
                <Spinner />
                Loading voice integration…
            </div>
        );
    }

    if (error || !setup) {
        return (
            <div className="mx-auto max-w-3xl p-6">
                <Alert variant="destructive">
                    <CircleAlert />
                    <AlertTitle>Voice integration not found</AlertTitle>
                    <AlertDescription>
                        This organization does not have a linked VoIP rollout
                        record yet.
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    const { account, onboarding, organization, provider_capabilities } = setup;
    // Same label the admin org picker shows: internal name when set,
    // otherwise the organization name.
    const organizationLabel =
        organization.internal_name?.trim() || organization.name;
    const providerLabel = account.phone_provider
        ? VOIP_PHONE_PROVIDER_LABELS[account.phone_provider]
        : organization.provider_display_name || 'Provider not selected';
    const officeNumber =
        setup.external_forwardings?.[0]?.external_main_number ?? '';
    const phoneTreeBrief = onboarding?.phone_tree_notes ?? '';
    const isQuo = provider_capabilities.provider_key === 'podium_quo';

    return (
        <div className="h-full max-w-full min-w-0 overflow-x-hidden overflow-y-auto bg-zinc-50/60">
            <div className="mx-auto w-full max-w-[1500px] min-w-0 space-y-6 p-5 lg:p-8">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <Link
                            to="/admin/voip"
                            className="text-muted-foreground hover:text-foreground mb-3 inline-flex items-center gap-2 text-sm"
                        >
                            <ArrowLeft className="size-4" /> Back to VOIP
                            dashboard
                        </Link>
                        <div className="flex flex-wrap items-center gap-2">
                            <h1 className="text-2xl font-semibold tracking-tight">
                                {organizationLabel}
                            </h1>
                            <Badge variant="outline">{providerLabel}</Badge>
                        </div>
                    </div>
                    <Button asChild={true} variant="outline">
                        <Link
                            to="/admin/organizations/$organization-id"
                            params={{ 'organization-id': organizationId }}
                        >
                            <Building2 className="size-4" /> Open organization
                        </Link>
                    </Button>
                </div>
                <SummaryCard
                    rows={[
                        ['Provider', organization.provider_display_name],
                        [
                            'Office integration',
                            onboarding?.wants_phoebe_in_phone_system,
                        ],
                        [
                            'Routing goal',
                            onboarding?.phone_system_integration_other ??
                                onboarding?.phone_system_integration,
                        ],
                        ['Portal email', onboarding?.provider_login_email],
                        ['External main line', officeNumber],
                        [
                            'Transfer destination',
                            organization.transfer_phone_number,
                        ],
                        ['CX owner', account.cx_owner],
                        ['Engineering owner', account.eng_owner],
                        ['Goal', account.goal],
                        ['Status', account.status_notes],
                    ]}
                />
                <VoipHealthCard organizationId={organizationId} />
                {/* The call-routing workflow preview and the desired call
                    flow card are intentionally hidden for now; the Quo
                    setup assistant remains the provider onboarding tool. */}
                {isQuo ? (
                    <VoipSetupAssistant
                        embedded={true}
                        organizationId={organizationId}
                        initialValues={{
                            accountName: account.account_name,
                            flowName: `${account.account_name} · Phoebe`,
                            officeNumber,
                            loginEmail:
                                onboarding?.provider_login_email ?? undefined,
                            brief: phoneTreeBrief || undefined,
                            timezone: organization.timezone,
                        }}
                    />
                ) : null}
            </div>
        </div>
    );
}

function SummaryCard({
    rows,
}: {
    readonly rows: ReadonlyArray<readonly [string, string | null | undefined]>;
}) {
    const filledRows = rows.filter(([, value]) => Boolean(value));
    if (filledRows.length === 0) {
        return null;
    }
    return (
        <Card className="shadow-none">
            <CardContent className="grid gap-x-8 gap-y-3 py-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
                {filledRows.map(([label, value]) => (
                    <DetailRow key={label} label={label}>
                        {value}
                    </DetailRow>
                ))}
            </CardContent>
        </Card>
    );
}
